import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { CheckCircle, Refresh, Edit, PersonOff, AdminPanelSettings } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAlertStyles } from '@/styles/utilities/hooks';
import { useAuth } from '@/shared/hooks/useAuth';
import api from '../../shared/services/api';
import { API_ENDPOINTS } from '../../shared/constants/apiEndpoints';
import ProfileEditDialog from './components/ProfileEditDialog';
import { userProfileService } from '../../shared/services/userProfileService';

export default function UserManagement() {
  const theme = useTheme();
  const alertStyles = useAlertStyles();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editDialogUser, setEditDialogUser] = useState(null);

  const fetchUsers = async () => {
    try {

      // Use the new /auth/users endpoint
      const response = await api.get(API_ENDPOINTS.AUTH.USERS);
      
      console.log('Users response:', response.data);
      
      // Handle paginated response format
      const users = response.data.items || response.data || [];
      setUsers(users);
      setError(''); // Clear any previous errors
      
      console.log('Processed users:', users);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      let errorMessage = 'Failed to fetch users. ';
      
      if (err.response?.status === 401) {
        errorMessage += 'Authentication failed. Please log in again.';
      } else if (err.response?.status === 403) {
        errorMessage += 'Access denied. Admin privileges required.';
      } else if (err.response?.status === 404) {
        errorMessage += 'Users endpoint not found. Please check if the API is updated.';
      } else if (err.response?.data?.detail) {
        errorMessage += err.response.data.detail;
      } else if (err.message) {
        errorMessage += err.message;
      } else {
        errorMessage += 'Unknown error occurred.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleToggleAdmin = async (user) => {
    // Safety check: prevent admin from removing their own admin privileges
    if (user.is_admin && currentUser?.username === user.username) {
      setError('Cannot revoke your own admin privileges');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setActionLoading(prev => ({ ...prev, [`admin-${user.username}`]: true }));
    setError('');

    try {
      const result = await userProfileService.updateUserByAdmin(user.username, {
        is_admin: !user.is_admin
      });

      if (result.success) {
        setSuccess(`User ${user.username} admin status updated`);
        await fetchUsers();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to update admin status');
    } finally {
      setActionLoading(prev => ({ ...prev, [`admin-${user.username}`]: false }));
    }
  };

  const handleToggleApproved = async (user) => {
    // Safety check: prevent admin from revoking their own approval
    if (user.is_approved && user.is_admin && currentUser?.username === user.username) {
      setError('Cannot revoke your own approval as an admin user');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setActionLoading(prev => ({ ...prev, [`approve-${user.username}`]: true }));
    setError('');

    try {
      const result = await userProfileService.updateUserByAdmin(user.username, {
        is_approved: !user.is_approved
      });

      if (result.success) {
        setSuccess(`User ${user.username} approval status updated`);
        await fetchUsers();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to update approval status');
    } finally {
      setActionLoading(prev => ({ ...prev, [`approve-${user.username}`]: false }));
    }
  };

  const handleEditUser = (user) => {
    setEditDialogUser(user);
  };

  const handleUserUpdate = async () => {
    // Refresh the user list after successful update
    await fetchUsers();
    setSuccess('User updated successfully');
    setTimeout(() => setSuccess(''), 3000);
  };



  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: theme.spacing(50),
        height: '100%'
      }}>
        <CircularProgress size={theme.dimensions.icon.large * 2} />
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex', 
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* Control Bar */}
      <Box sx={{
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        gap: theme.spacing(2),
        px: theme.spacing(2),
        py: theme.spacing(1),
        height: theme.dimensions.panel.headerHeight,
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
        backgroundColor: theme.palette.grey[50]
      }}>
        {/* User Statistics */}
        {users.length > 0 && (
          <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
            <Chip
              label={`Total Users: ${users.length}`}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ height: theme.dimensions.chip.small }}
            />
            <Chip
              label={`Approved: ${users.filter(u => u.is_approved).length}`}
              color="success"
              variant="outlined"
              size="small"
              sx={{ height: theme.dimensions.chip.small }}
            />
            <Chip
              label={`Pending: ${users.filter(u => !u.is_approved).length}`}
              color="warning"
              variant="outlined"
              size="small"
              sx={{ height: theme.dimensions.chip.small }}
            />
            <Chip
              label={`Admins: ${users.filter(u => u.is_admin).length}`}
              color="info"
              variant="outlined"
              size="small"
              sx={{ height: theme.dimensions.chip.small }}
            />
          </Box>
        )}

        <Button
          variant="outlined"
          startIcon={<Refresh sx={{ fontSize: theme.dimensions.icon.small }} />}
          onClick={fetchUsers}
          disabled={loading}
          size="small"
          sx={{ height: theme.dimensions.button.small }}
        >
          Refresh
        </Button>
      </Box>

      {/* Main Content Area */}
      <Box sx={{
        flexGrow: 1,
        overflow: 'auto',
        p: theme.spacing(2)
      }}>

        {error && (
          <Alert severity="error" sx={alertStyles.root}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={alertStyles.root}>
            {success}
          </Alert>
        )}


        {users.length === 0 ? (
          <Typography 
            color="text.secondary" 
            sx={{ 
              textAlign: 'center', 
              py: theme.spacing(4),
              fontSize: theme.typography.fontSize.base
            }}
          >
            No users found
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center" sx={{ minWidth: theme.spacing(25) }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.username}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.fullname || '-'}</TableCell>
                    <TableCell>{user.company || '-'}</TableCell>
                    <TableCell>{user.department || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_admin ? 'Admin' : 'User'}
                        color={user.is_admin ? 'primary' : 'default'}
                        size="small"
                        sx={{ height: theme.dimensions.chip.small }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_approved ? 'Approved' : 'Pending'}
                        color={user.is_approved ? 'success' : 'warning'}
                        size="small"
                        sx={{ height: theme.dimensions.chip.small }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: theme.spacing(0.5), justifyContent: 'center' }}>
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            onClick={() => handleEditUser(user)}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={
                          user.is_approved
                            ? (user.is_admin && currentUser?.username === user.username
                                ? "Cannot revoke own approval (admin user)"
                                : "Revoke Approval")
                            : "Approve User"
                        }>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleApproved(user)}
                              disabled={
                                actionLoading[`approve-${user.username}`] ||
                                (user.is_approved && user.is_admin && currentUser?.username === user.username)
                              }
                              sx={{
                                color: user.is_approved
                                  ? (user.is_admin && currentUser?.username === user.username
                                      ? theme.palette.action.disabled
                                      : theme.palette.warning.main)
                                  : theme.palette.success.main
                              }}
                            >
                              {user.is_approved ? <PersonOff fontSize="small" /> : <CheckCircle fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title={
                          user.is_admin
                            ? (currentUser?.username === user.username
                                ? "Cannot revoke own admin privileges"
                                : "Revoke Admin")
                            : "Grant Admin"
                        }>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleAdmin(user)}
                              disabled={
                                actionLoading[`admin-${user.username}`] ||
                                (user.is_admin && currentUser?.username === user.username)
                              }
                              sx={{
                                color: user.is_admin
                                  ? (currentUser?.username === user.username
                                      ? theme.palette.action.disabled
                                      : theme.palette.error.main)
                                  : theme.palette.info.main
                              }}
                            >
                              <AdminPanelSettings fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

      </Box>

      {/* User Edit Dialog */}
      {editDialogUser && (
        <ProfileEditDialog
          open={Boolean(editDialogUser)}
          onClose={() => setEditDialogUser(null)}
          user={editDialogUser}
          isAdminMode={true}
          onUserUpdate={handleUserUpdate}
          currentUser={currentUser}
        />
      )}
    </Box>
  );
}